import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, tap, finalize } from 'rxjs/operators';
import { startWith } from 'rxjs/operators';
import { PersonaService } from '@services/persona/persona.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { PersonaDetailDto } from '@models/persona/personaDetailDto.model';
import { PersonaDto } from '@models/persona/personaDto.model';
import { DialogCreatePersonaSimpleComponent } from '@views/personas/dialog-create-persona-simple/dialog-create-persona-simple.component';
import { MatDialog } from '@angular/material/dialog';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';

@Component({
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    ReactiveFormsModule,
    NgxSpinnerModule,
    CommonModule
  ],
  selector: 'app-buscar-persona',
  templateUrl: './buscar-persona.component.html',
  styleUrls: ['./buscar-persona.component.css']
})
export class BuscarPersonaComponent implements OnInit, OnChanges {

  public filteredPersonas!: Observable<PersonaDto[]>;
  personas: PersonaDto[] = [];
  personaDetailDto!: PersonaDetailDto;
  public entityControl = new FormControl('');

  @Output() eventEntity = new EventEmitter<PersonaDetailDto>();
  @Input() personaEntrada: PersonaDetailDto = new PersonaDetailDto();
  @Input() idPersonaInput!: number | null;
  @Input() disable: boolean = false;
  @Input() nombre: string = "Persona";
  @Input() loadBar: boolean = true;

  private idPersona?: number;
  entitySelect!: number | null;
  disabledDocumento: FormControl = new FormControl();
  esPersona: boolean = false;

  personas$!: Observable<PersonaDetailDto[]>;
  searchControl = new FormControl('');

  constructor(
    public dialog: MatDialog,
    private personaService: PersonaService,
    private spinner: NgxSpinnerService,
    private snackBar: SnackBarService,
    private router: Router,
    private formBuilder: FormBuilder,
  ) {
    this.eventEntity = new EventEmitter<PersonaDetailDto>();
  }

  ngOnInit(): void {
    this.setupPersonaSearch();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['idPersonaInput'] && this.idPersonaInput && this.idPersonaInput !== 0) {
      this.getPersonaById(this.idPersonaInput);
    }

    if (changes['personaEntrada'] && this.personaEntrada?.nombreCompleto) {
      this.entityControl.setValue(this.personaEntrada.nombreCompleto);
    }
  }

  private setupPersonaSearch(): void {
    this.personas$ = this.searchControl.valueChanges.pipe(
      startWith(''),
      distinctUntilChanged(),
      debounceTime(300),
      filter((searchTerm) => {
        // Solo buscar si hay al menos 2 caracteres
        if (!searchTerm || searchTerm.length < 2) {
          return false;
        }
        return true;
      }),
      tap(() => {
        if (this.loadBar) {
          this.spinner.show("spBusquedaPersona");
        }
      }),
      switchMap(searchTerm =>
        this.personaService.getAllByName(searchTerm || '').pipe(
          catchError(error => {
            console.error('Error en búsqueda de personas:', error);
            this.snackBar.showError('Error al buscar personas', "Error");
            return of([]); // Retornar array vacío en caso de error
          }),
          finalize(() => {
            if (this.loadBar) {
              this.spinner.hide("spBusquedaPersona");
            }
          })
        )
      )
    );
  }

  onNuevaPersona(): void {
    const dialogRef = this.dialog.open(DialogCreatePersonaSimpleComponent, {
      width: '600px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.data) {
        // Si se creó una nueva persona, seleccionarla
        this.selectValue(result.data);
      }
    });
  }

  selectValue(persona: PersonaDetailDto): void {
    if (persona) {
      this.personaEntrada = persona;
      this.entityControl.setValue(persona.nombreCompleto);
      this.esPersona = true;
      this.eventEntity.emit(persona);
    }
  }

  onPersonaSelected(persona: PersonaDetailDto): void {
    this.selectValue(persona);
  }

  displayPersona(persona: PersonaDetailDto): string {
    return persona?.nombreCompleto || '';
  }

  getAllPersona(): void {
    this.spinner.show("spBusquedaPersona");
    this.personaService.getAll()
      .subscribe({
        next: (data) => {
          this.spinner.hide("spBusquedaPersona");
          this.personas = data;
        },
        error: (error) => {
          this.spinner.hide("spBusquedaPersona");
          this.snackBar.showError(error, "Error");
        }
      });
  }

  getPersonaById(id: number | null): void {
    if (!id || id === 0) {
      return;
    }

    this.spinner.show("spBusquedaPersona");
    this.personaService.getPersonaById(id)
      .subscribe({
        next: (data) => {
          this.spinner.hide("spBusquedaPersona");
          this.personaEntrada = data;
          this.entityControl.setValue(data.nombreCompleto);
          this.eventEntity.emit(data);
          this.personaDetailDto = data;
          this.esPersona = true;
        },
        error: (error) => {
          this.spinner.hide("spBusquedaPersona");
          this.snackBar.showError(error, "Error");
        }
      });
  }

  editarPersona(idPersona: number): void {
    this.getPersonaByIdAndRoute(idPersona);
  }

  getPersonaByIdAndRoute(idPersona: number): void {
    this.spinner.show("spPersona");
    this.personaService.getPersonaById(idPersona)
      .subscribe({
        next: (data) => {
          this.spinner.hide("spPersona");
          this.personaService.setPersona(data);
          this.router.navigate(["/personas/edit"]);
        },
        error: (error) => {
          this.spinner.hide("spPersona");
          this.snackBar.showError(error, "Error");
        }
      });
  }

  clearSelection(): void {
    this.entityControl.setValue('');
    this.personaEntrada = new PersonaDetailDto();
    this.esPersona = false;
    this.eventEntity.emit(new PersonaDetailDto());
  }

  trackByItems(index: number, item: any): any {
    return item?.id || index;
  }
}
